import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Avatar,
  LinearProgress,
  Chip,
  IconButton,
  TextField,
  Dialog,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";
import { Check } from "lucide-react";
import { showToast } from "../../../../utils/toast";
import { useAppSelector } from "../../../../redux/typedHooks";
import { UserInterface } from "../../../../types/user";
import {
  getUserReferralsAPI,
  getFileByIdAPI,
  sendReferralEmailAPI,
} from "../../../../api/userApi";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import whatsappIcon from "../../../../assets/images/icons/whatIcon.png";
import xIcon from "../../../../assets/images/icons/xIcon.png";
import facebookIcon from "../../../../assets/images/icons/facebookIcon.png";
import gmailIcon from "../../../../assets/images/icons/gmailIcon.png";
import discordIcon from "../../../../assets/images/icons/discordIcon.png";
import referralProgramIcon from "../../../../assets/images/icons/refferalProgramIcon.svg";
import calmIcon from "../../../../assets/images/icons/calm.png";
import inviteEnvelopeIcon from "../../../../assets/images/icons/envelope 1.png";

const maxEarnings = 400;
const earningsPerReferral = 10;

const useStyles = makeStyles(theme => ({
  pageContainer: {
    minHeight: "100vh",
    padding: "2rem 0",
    [theme.breakpoints.down("sm")]: {
      padding: "1rem 0",
    },
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1rem",
  },
  pageHeader: {
    textAlign: "left",
    marginBottom: "3rem",
    color: "white",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "2rem",
    },
  },
  pageTitle: {
    fontSize: "40px",
    fontWeight: 700,
    marginBottom: "12px",
    color: "#ffffff",
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "110%",
    },
  },
  pageDescription: {
    fontSize: "20px",
    fontWeight: 400,
    color: "white",
    textAlign: "left",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      color: "#FFFFFF",
      fontWeight: 400,
      lineHeight: "110%",
      display: "block",
    },
  },
  contentGrid: {
    display: "flex",
    gap: "2rem",
    marginTop: "3rem",
    alignItems: "flex-start",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column-reverse",
      gap: "1.5rem",
    },
  },
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    minWidth: "280px",
    maxWidth: "280px",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      maxWidth: "100%",
      minWidth: "100%",
    },
  },
  card: {
    backgroundColor: "#131313",
    borderRadius: 0,
    padding: "20px",
    color: "white",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #363636",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 16px",
    },
  },
  referralsCard: {
    backgroundColor: "#131313",
    borderRadius: "16px",
    padding: "20px",
    color: "white",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #363636",
    [theme.breakpoints.down("sm")]: {
      backgroundColor: "transparent",
      border: "none",
      padding: "0",
    },
  },
  howItWorksCard: {
    backgroundColor: "#131313",
    borderRadius: "16px",
    padding: "20px",
    color: "white",
    height: "inherit",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #363636",
    [theme.breakpoints.down("sm")]: {
      padding: "0",
      backgroundColor: "transparent",
      border: "none",
    },
  },
  cardTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1rem",
    color: "#FE5A97",
  },
  howItWorksTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "1.5rem",
    color: "#ffffff",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  howItWorksList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      gap: "1rem",
    },
  },
  howItWorksItem: {
    display: "flex",
    gap: "8px",
    paddingBottom: "1.5rem",
    position: "relative",
    "&:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      left: "20px",
      top: "40px",
      width: "1px",
      height: "calc(100% - 16px)",
      backgroundColor: "#FFFFFF1A",
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      textAlign: "left",
      paddingBottom: 0,
      paddingRight: "1rem",
      flex: 1,
      position: "relative",
      "&:not(:last-child)::after": {
        content: '""',
        position: "absolute",
        left: "20px",
        top: "40px",
        right: "0.5rem",
        height: "1px",
        backgroundColor: "#FFFFFF1A",
      },
    },
  },
  howItWorksNumber: {
    minWidth: "40px",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #EF599680",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "transparent",
    flexShrink: 0,
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      fontWeight: 400,
      color: "#FFFFFF",
    },
  },
  howItWorksContent: {
    flex: 1,
    paddingTop: "0.25rem",
    [theme.breakpoints.down("sm")]: {
      paddingTop: 0,
    },
  },
  howItWorksItemTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "0.5rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      fontWeight: 500,
      color: "white",
    },
  },
  howItWorksItemText: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.8)",
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  referralLinkContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    marginTop: "1rem",
    backgroundColor: "#0C0A09",
    padding: "8px",
    borderRadius: "8px",
    [theme.breakpoints.down("sm")]: {
      marginTop: 0,
      justifyContent: "flex-end",
      gap: "0.75rem",
    },
    "@media (max-width: 400px)": {
      padding: "6px",
      gap: "0.5rem",
    },
  },
  referralLinkLabel: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#ffffff",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  referralLinkText: {
    fontSize: "16px",
    fontWeight: 400,
    color: "#ffffff",
    whiteSpace: "nowrap",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100% - 60px)",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "@media (max-width: 400px)": {
      maxWidth: "calc(100% - 50px)",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "14px",
    },
  },
  copyButton: {
    color: "#ffffff",
    minWidth: "64px",
    minHeight: "60px",
    width: "64px",
    height: "60px",
    padding: "0.5rem",
    backgroundColor: "#FE5A97",
    borderRadius: "6px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  referralInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent",
      color: "white",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "white",
      fontSize: "16px",
    },
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "1.5rem 0",
    "&::before": {
      content: '""',
      flex: 1,
      height: "1px",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  dividerText: {
    padding: "0 1rem",
    color: "white",
    fontSize: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  desktopEmailSection: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  emailLabel: {
    fontSize: "20px",
    fontWeight: 400,
    color: "#ffffff",
    marginBottom: "0.75rem",
  },
  emailContainer: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  emailInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#0C0A09",
      color: "white",
      borderRadius: "8px",
      height: "60px",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "white",
      fontSize: "16px",
      padding: "12px 16px",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  sendButton: {
    backgroundColor: "#FE5A97",
    color: "white",
    padding: "0 24px",
    minWidth: "126px",
    height: "60px",
    borderRadius: "8px",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d155a9",
    },
    "&:disabled": {
      backgroundColor: "rgba(254, 90, 151, 0.5)",
      color: "rgba(255, 255, 255, 0.5)",
    },
  },
  inviteSuccessPopup: {
    backgroundColor: "#131313",
    borderRadius: "24px",
    padding: "32px 24px",
    minWidth: "590px",
    maxWidth: "590px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      minWidth: "260px",
      maxWidth: "320px",
      padding: "24px 16px",
    },
  },
  inviteSuccessIcon: {
    width: 80,
    height: 80,
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      width: 64,
      height: 64,
      marginBottom: "16px",
    },
  },
  inviteSuccessTitle: {
    fontSize: "40px",
    fontWeight: 700,
    color: "#FFFFFF",
    marginBottom: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  inviteSuccessSubtitle: {
    fontSize: "20px",
    fontWeight: 400,
    color: "#FFFFFF",
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  inviteSuccessButton: {
    width: "161px",
    backgroundColor: "#FE5A97",
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "20px",
    textTransform: "none",
    borderRadius: "12px",
    "&:hover": {
      backgroundColor: "#d155a9",
    },
  },
  mobileShareContainer: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      marginTop: 0,
    },
  },
  mobileShareTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "white",
    marginBottom: "0.75rem",
  },
  mobileShareIcons: {
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  mobileShareIconButton: {
    width: 40,
    height: 40,
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
  },
  mobileShareIconWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    textDecoration: "none",
  },
  mobileShareIconLabel: {
    fontSize: "12px",
    fontWeight: 400,
    color: "#ffffff",
    textAlign: "center",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "1rem",
    marginTop: "1.5rem",
  },
  statItem: {
    textAlign: "center",
    padding: "1rem",
    backgroundColor: "#2A2A2A",
    borderRadius: "8px",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#FE5A97",
    marginBottom: "0.5rem",
  },
  statLabel: {
    fontSize: "0.9rem",
    color: "rgba(255, 255, 255, 0.7)",
  },
  listItem: {
    padding: "0.75rem 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  listText: {
    fontSize: "0.95rem",
    color: "rgba(255, 255, 255, 0.9)",
  },
  filterContainer: {
    display: "flex",
    gap: "1rem",
    marginBottom: "1rem",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filterSelect: {
    minWidth: "150px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#2A2A2A",
      color: "white",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.3)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FE5A97",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiSelect-icon": {
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  referralsList: {
    maxHeight: "500px",
    overflowY: "auto",
    marginTop: "1rem",
  },
  referralItem: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "1rem",
    backgroundColor: "transparent",
    borderBottom: "1px solid #363636",
    borderRadius: "0",
    marginBottom: "0.75rem",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  referralAvatar: {
    width: 48,
    height: 48,
  },
  referralInfo: {
    flex: 1,
  },
  referralName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "white",
    marginBottom: "0.25rem",
  },
  referralDate: {
    fontSize: "0.85rem",
    color: "rgba(255, 255, 255, 0.6)",
  },
  referralStatus: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 500,
  },
  statusPending: {
    color: "#fff",
    backgroundColor: "#232323",
  },
  statusPaid: {
    backgroundColor: "rgba(76, 175, 80, 0.2)",
    color: "#4CAF50",
  },
  statusCancelled: {
    backgroundColor: "rgba(244, 67, 54, 0.2)",
    color: "#F44336",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "0.5rem",
    marginTop: "1rem",
  },
  paginationButton: {
    minWidth: "40px",
    color: "white",
    borderColor: "rgba(255, 255, 255, 0.2)",
    "&:hover": {
      borderColor: "#FE5A97",
      backgroundColor: "rgba(254, 90, 151, 0.1)",
    },
    "&.Mui-disabled": {
      borderColor: "rgba(255, 255, 255, 0.1)",
      color: "rgba(255, 255, 255, 0.3)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    maxWidth: "380px",
    margin: "0 auto",
    "& .empty-state-icon": {
      width: 140,
      height: 140,
      [theme.breakpoints.down("sm")]: {
        width: 120,
        height: 120,
      },
    },
  },
  headerWithProgress: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
    gap: "1rem",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      alignItems: "center",
    },
  },
  headerTitle: {
    color: "#fff",
    fontWeight: 600,
    fontSize: "1.5rem",
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    flex: 1,
    minWidth: "200px",
    maxWidth: "400px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-end",
    },
  },
  progressBar: {
    flex: 1,
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#FFFFFF1A",
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#FE5A97",
      borderRadius: "4px",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  progressText: {
    fontSize: "20px",
    fontWeight: 400,
    whiteSpace: "nowrap",
    color: "rgba(255, 255, 255, 0.9)",
  },
  awardChip: {
    marginTop: "1rem",
    marginBottom: "1rem",
    borderRadius: "12px",
    border: "1px solid #FF80C0",
    backgroundColor: "#FFFFFF0D",
    color: "#ffffff",
    fontWeight: 500,
    fontSize: "0.875rem",
    padding: "0.5rem 1rem",
    "&:hover": {
      backgroundColor: "#e04a7f",
    },
  },
}));

interface ReferredUser {
  id: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImg: string | null;
  email: string | null;
}

interface Referral {
  id: string;
  amount: number;
  createdAt: string;
  paidAt: string | null;
  referredUser: ReferredUser;
  status: "pending" | "paid" | "cancelled";
}

interface ReferralsResponse {
  success: boolean;
  referralId: number;
  userId: string;
  referrals: Referral[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const ReferralProgram: React.FC = () => {
  const seo = useSEO("referral");
  const classes = useStyles();
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const [referralLink, setReferralLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState<ReferralsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState<Map<string, string>>(new Map());
  const [inviteSuccessOpen, setInviteSuccessOpen] = useState(false);
  const limit = 10;
  const statusFilter = "all"; // Show all referrals

  const absoluteUrlPattern = /^(https?:|data:|blob:)/i;

  useEffect(() => {
    // Generate referral link based on user
    if (user?.referralId) {
      const baseUrl = window.location.origin;
      const refCode = user.referralId;
      setReferralLink(`${baseUrl}/register?ref=${refCode}`);
    }
  }, [user]);

  const fetchReferrals = useCallback(async () => {
    if (!user || !user.id) return;
    setLoading(true);
    try {
      const options: any = {
        page,
        limit,
      };
      if (statusFilter !== "all") {
        options.status = statusFilter;
      }
      const data = await getUserReferralsAPI(user.id, options);
      setReferrals(data);
    } catch (error: any) {
      console.error("Failed to fetch referrals:", error);
      showToast(
        error?.response?.data?.error || "Failed to load referrals",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, user]);

  // Calculate stats from referrals data
  const totalReferrals = referrals?.pagination?.total || 0;
  const progressPercentage = Math.min(
    ((totalReferrals * earningsPerReferral) / maxEarnings) * 100,
    100
  );

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // Fetch avatar images for referrals
  useEffect(() => {
    if (!referrals?.referrals) return;

    const fetchAvatars = async () => {
      const newAvatarUrls = new Map<string, string>();
      const loadingSet = new Set<string>();

      for (const referral of referrals.referrals) {
        const profileImg = referral.referredUser?.profileImg;
        if (!profileImg) continue;

        // Skip if is Google URL (will be used directly)
        if (profileImg.includes("lh3.googleusercontent.com")) {
          continue;
        }

        // Skip if is absolute URL (will be used directly)
        if (absoluteUrlPattern.test(profileImg)) {
          continue;
        }

        // Check if it's an API file path
        if (profileImg.startsWith("/api/files/")) {
          const fileId = profileImg.split("/api/files/")[1];
          if (fileId && !loadingSet.has(profileImg)) {
            loadingSet.add(profileImg);
            try {
              const blobUrl = await getFileByIdAPI(fileId);
              newAvatarUrls.set(profileImg, blobUrl);
            } catch (error) {
              console.error(
                `Failed to fetch avatar for referral ${referral.id}:`,
                error
              );
              // Don't set anything, will use placeholder
            } finally {
              loadingSet.delete(profileImg);
            }
          }
        } else {
          // Direct URL, use as is
          newAvatarUrls.set(profileImg, profileImg);
        }
      }

      if (newAvatarUrls.size > 0) {
        setAvatarUrls(prev => {
          const updated = new Map(prev);
          let hasNew = false;
          newAvatarUrls.forEach((value, key) => {
            if (!updated.has(key)) {
              updated.set(key, value);
              hasNew = true;
            }
          });
          return hasNew ? updated : prev;
        });
      }
    };

    fetchAvatars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrals?.referrals]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case "pending":
        return classes.statusPending;
      case "paid":
        return classes.statusPaid;
      case "cancelled":
        return classes.statusCancelled;
      default:
        return classes.statusPending;
    }
  };

  const getDisplayName = (referredUser: ReferredUser) => {
    if (referredUser.firstName && referredUser.lastName) {
      return `${referredUser.firstName} ${referredUser.lastName}`;
    }
    if (referredUser.firstName) {
      return referredUser.firstName;
    }
    if (referredUser.username) {
      return `@${referredUser.username}`;
    }
    return "Unknown User";
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast("Link copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showToast("Failed to copy link", "error");
    }
  };

  const handleSendEmail = async () => {
    if (!email || !email.includes("@")) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    setSendingEmail(true);
    try {
      await sendReferralEmailAPI(email, referralLink);
      showToast("Referral link sent to email!", "success");
      setEmail("");
      setInviteSuccessOpen(true);
    } catch (err: any) {
      showToast(err?.message || err?.error || "Failed to send email", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Dialog
        open={inviteSuccessOpen}
        onClose={() => setInviteSuccessOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box className={classes.inviteSuccessPopup}>
          <img
            src={inviteEnvelopeIcon}
            alt="Invitation sent"
            className={classes.inviteSuccessIcon}
          />
          <Typography className={classes.inviteSuccessTitle}>
            Your invitation were successfuly send!
          </Typography>
          <Typography className={classes.inviteSuccessSubtitle}>
            Invite more friends to share a content and earn together
          </Typography>
          <Button
            className={classes.inviteSuccessButton}
            onClick={() => setInviteSuccessOpen(false)}
          >
            Continue
          </Button>
        </Box>
      </Dialog>
      <Box className={classes.pageContainer}>
        <Box className={classes.container}>
          <Box className={classes.pageHeader}>
            <Typography className={classes.pageTitle}>
              Invite your friends and earn up to $400!
            </Typography>
            <Typography className={classes.pageDescription}>
              Share a link to receive $10 for each registered friend
            </Typography>
          </Box>

          <Box className={classes.contentGrid}>
            {/* Left Column */}
            <Box className={classes.leftColumn}>
              {/* Block 1: Referral Link */}
              <Paper className={classes.card}>
                <Box className={classes.cardContent}>
                  <Box className={classes.referralLinkContainer}>
                    <Typography className={classes.referralLinkLabel}>
                      Link
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "24px",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography className={classes.referralLinkText}>
                        {referralLink}
                      </Typography>
                      <IconButton
                        className={classes.copyButton}
                        onClick={handleCopy}
                        aria-label="Copy link"
                      >
                        {copied ? (
                          <Check size={20} />
                        ) : (
                          <img
                            src={referralProgramIcon}
                            alt="Copy link"
                            width={24}
                            height={24}
                          />
                        )}
                      </IconButton>
                    </div>
                  </Box>

                  <Box className={classes.divider}>
                    <Typography className={classes.dividerText}>or</Typography>
                  </Box>

                  <Box className={classes.desktopEmailSection}>
                    <Typography className={classes.emailLabel}>
                      Invite via e-mail
                    </Typography>
                    <Box className={classes.emailContainer}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className={classes.emailInput}
                        disabled={sendingEmail}
                        onKeyPress={e => {
                          if (e.key === "Enter") {
                            handleSendEmail();
                          }
                        }}
                      />
                      <Button
                        className={classes.sendButton}
                        onClick={handleSendEmail}
                        disabled={sendingEmail || !email}
                      >
                        {sendingEmail ? "Sending..." : "Send"}
                      </Button>
                    </Box>
                  </Box>

                  <Box className={classes.mobileShareContainer}>
                    <Typography className={classes.mobileShareTitle}>
                      Share to
                    </Typography>
                    <Box className={classes.mobileShareIcons}>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(
                          referralLink
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileShareIconWrapper}
                      >
                        <div className={classes.mobileShareIconButton}>
                          <img
                            src={whatsappIcon}
                            alt="WhatsApp"
                            width={40}
                            height={40}
                          />
                        </div>
                        <Typography className={classes.mobileShareIconLabel}>
                          WhatsApp
                        </Typography>
                      </a>
                      <a
                        href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
                          "Join me on Sugar Cribs"
                        )}&body=${encodeURIComponent(referralLink)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileShareIconWrapper}
                      >
                        <div className={classes.mobileShareIconButton}>
                          <img
                            src={gmailIcon}
                            alt="Gmail"
                            width={40}
                            height={40}
                          />
                        </div>
                        <Typography className={classes.mobileShareIconLabel}>
                          Gmail
                        </Typography>
                      </a>
                      <a
                        href={`https://discord.com/channels/@me`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileShareIconWrapper}
                      >
                        <div className={classes.mobileShareIconButton}>
                          <img
                            src={discordIcon}
                            alt="Discord"
                            width={40}
                            height={40}
                          />
                        </div>
                        <Typography className={classes.mobileShareIconLabel}>
                          Discord
                        </Typography>
                      </a>
                      <a
                        href={`https://x.com/intent/tweet?text=${encodeURIComponent(
                          `Join me on Sugar Cribs: ${referralLink}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileShareIconWrapper}
                      >
                        <div className={classes.mobileShareIconButton}>
                          <img src={xIcon} alt="X" width={40} height={40} />
                        </div>
                        <Typography className={classes.mobileShareIconLabel}>
                          X
                        </Typography>
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          referralLink
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileShareIconWrapper}
                      >
                        <div className={classes.mobileShareIconButton}>
                          <img
                            src={facebookIcon}
                            alt="Facebook"
                            width={40}
                            height={40}
                          />
                        </div>
                        <Typography className={classes.mobileShareIconLabel}>
                          Facebook
                        </Typography>
                      </a>
                    </Box>
                  </Box>

                  <Box className={classes.statsGrid}></Box>
                </Box>
              </Paper>

              {/* Block 2: Referrals List */}
              <Paper className={classes.referralsCard}>
                <Box className={classes.headerWithProgress}>
                  <Typography className={classes.headerTitle}>
                    Invited friends:
                  </Typography>
                  <Box className={classes.progressContainer}>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercentage}
                      className={classes.progressBar}
                    />
                    <Typography className={classes.progressText}>
                      <span style={{ color: "#FFFFFF4A" }}>
                        ${totalReferrals * earningsPerReferral}{" "}
                      </span>
                      <b>/ {maxEarnings} Earned</b>
                    </Typography>
                  </Box>
                </Box>

                {totalReferrals >= maxEarnings / earningsPerReferral && (
                  <Box display={"flex"} justifyContent={"end"}>
                    <Box maxWidth={"400px"}>
                      <Chip
                        label="Invite 5 friends and get a special award for your profile!"
                        className={classes.awardChip}
                      />
                    </Box>
                  </Box>
                )}

                <Box className={classes.cardContent}>
                  {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      style={{ minHeight: "200px" }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : referrals?.referrals && referrals.referrals.length > 0 ? (
                    <>
                      <Box className={classes.referralsList}>
                        {referrals?.referrals?.map(referral => {
                          const displayName = getDisplayName(
                            referral.referredUser
                          );
                          const profileImg = referral.referredUser?.profileImg;

                          // Determine avatar source
                          let avatarSrc = defaultAvatars;
                          if (profileImg) {
                            if (
                              profileImg.includes("lh3.googleusercontent.com")
                            ) {
                              // Google URL - use directly
                              avatarSrc = profileImg;
                            } else if (absoluteUrlPattern.test(profileImg)) {
                              // Absolute URL (https, data, blob)
                              avatarSrc = profileImg;
                            } else if (profileImg.startsWith("/api/files/")) {
                              // API file path - check if we have resolved URL
                              const resolvedUrl = avatarUrls.get(profileImg);
                              avatarSrc = resolvedUrl || defaultAvatars;
                            } else {
                              // Direct relative URL
                              avatarSrc = profileImg;
                            }
                          }

                          const initials =
                            referral.referredUser?.firstName?.[0] ||
                            referral.referredUser?.username?.[0] ||
                            "U";
                          const showPlaceholder =
                            !profileImg ||
                            (profileImg.startsWith("/api/files/") &&
                              !avatarUrls.has(profileImg));

                          return (
                            <Box
                              key={referral.id}
                              className={classes.referralItem}
                            >
                              <Avatar
                                src={avatarSrc}
                                className={classes.referralAvatar}
                                onError={e => {
                                  // Fallback to placeholder on image load error
                                  const target = e.target as HTMLImageElement;
                                  target.src = defaultAvatars;
                                }}
                              >
                                {showPlaceholder && initials}
                              </Avatar>
                              <Box className={classes.referralInfo}>
                                <Typography className={classes.referralName}>
                                  {displayName}
                                  {referral.referredUser?.username && (
                                    <span
                                      style={{
                                        color: "rgba(255, 255, 255, 0.6)",
                                        fontWeight: 400,
                                        marginLeft: "0.5rem",
                                      }}
                                    >
                                      (@{referral.referredUser.username})
                                    </span>
                                  )}
                                </Typography>
                                <Typography className={classes.referralDate}>
                                  {new Date(
                                    referral.createdAt
                                  ).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  gap: "0.25rem",
                                }}
                              >
                                <Typography
                                  className={`${
                                    classes.referralStatus
                                  } ${getStatusClass(referral.status)}`}
                                >
                                  <Check size={20} color="green" />
                                  Registered
                                </Typography>
                                {referral.status === "paid" && (
                                  <Typography
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#4CAF50",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ${referral.amount}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      {referrals?.pagination &&
                        referrals.pagination.pages > 1 && (
                          <Box className={classes.paginationContainer}>
                            <Button
                              variant="outlined"
                              className={classes.paginationButton}
                              disabled={page === 1}
                              onClick={() => setPage(p => p - 1)}
                            >
                              Previous
                            </Button>
                            <Typography
                              style={{
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                padding: "0 1rem",
                              }}
                            >
                              Page {page} of {referrals.pagination.pages}
                            </Typography>
                            <Button
                              variant="outlined"
                              className={classes.paginationButton}
                              disabled={page >= referrals.pagination.pages}
                              onClick={() => setPage(p => p + 1)}
                            >
                              Next
                            </Button>
                          </Box>
                        )}
                    </>
                  ) : (
                    <Box className={classes.emptyState}>
                      <img
                        src={calmIcon}
                        alt="No referrals yet"
                        style={{ width: 140, height: 140 }}
                        className="empty-state-icon"
                      />
                      <Typography
                        variant="body1"
                        style={{ color: "#FFFFFF" }}
                      >
                        You friends haven`t joined Sugar Cribs yet. Share a link and
                        earn together!
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Right Column - How It Works */}
            <Box className={classes.rightColumn}>
              <Paper className={classes.howItWorksCard}>
                <Typography className={classes.howItWorksTitle}>
                  How It Works
                </Typography>
                <Box className={classes.cardContent}>
                  <Box className={classes.howItWorksList}>
                    <Box className={classes.howItWorksItem}>
                      <Box className={classes.howItWorksNumber}>01</Box>
                      <Box className={classes.howItWorksContent}>
                        <Typography className={classes.howItWorksItemTitle}>
                          Share your link
                        </Typography>
                        <Typography className={classes.howItWorksItemText}>
                          Send your personal link to friends who haven't used
                          Sugar Cribs yet
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.howItWorksItem}>
                      <Box className={classes.howItWorksNumber}>02</Box>
                      <Box className={classes.howItWorksContent}>
                        <Typography className={classes.howItWorksItemTitle}>
                          Your friend join
                        </Typography>
                        <Typography className={classes.howItWorksItemText}>
                          Once your friend sign up using your link, they'll
                          appear on your invited list
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.howItWorksItem}>
                      <Box className={classes.howItWorksNumber}>03</Box>
                      <Box className={classes.howItWorksContent}>
                        <Typography className={classes.howItWorksItemTitle}>
                          Your earn $10
                        </Typography>
                        <Typography className={classes.howItWorksItemText}>
                          For each successful signup, you'll get a $10 bonus in
                          your account —easy as that!
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ReferralProgram;
